import { useEffect, useState, useRef } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '../contexts/LanguageContext'
import { getApiUrl } from '../lib/utils'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Textarea } from '../components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog'
import {
  Loader2,
  ArrowRight,
  Calendar,
  CalendarCheck,
  Gift,
  Languages,
  Check,
  CheckCircle2,
  KeyRound,
  Ticket,
} from 'lucide-react'
import { captureReferralFromUrl, normalizeReferralCode } from '../lib/referral'
import { toast } from 'sonner'
import LanguageToggle from '../components/LanguageToggle'
import ThemeMenu from '../components/ThemeMenu'
import AppFooter from '../components/AppFooter'
import ShowcaseSlideshow from '../components/landing/ShowcaseSlideshow'
import { Magnetic, Tilt } from '../components/celebration'

const API = getApiUrl()

const Login = () => {
  const location = useLocation()
  // Where to go after signing in. The template gallery's "Use this template"
  // sends a signed-out visitor here with ?next=/events/create?template=… so
  // their pick survives the sign-in. Only same-origin paths are honoured.
  const nextPath = (() => {
    const raw = new URLSearchParams(location.search).get('next') || ''
    return raw.startsWith('/') && !raw.startsWith('//') ? raw : ''
  })()
  // /register (and any ?ref= referral link, which always points there) opens
  // straight on the signup form; /login keeps the sign-in default.
  const [isLogin, setIsLogin] = useState(location.pathname !== '/register')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [loading, setLoading] = useState(false)
  const { login, register, loginWithGoogle } = useAuth()
  const { t, language } = useLanguage()
  const navigate = useNavigate()
  const authRef = useRef<HTMLDivElement>(null)
  const emailRef = useRef<HTMLInputElement>(null)

  // Referral code: pre-filled from ?ref= (or one remembered earlier this
  // session), still editable so somebody handed a code verbally can type it.
  const [referralCode, setReferralCode] = useState(() => captureReferralFromUrl())
  const [referrerName, setReferrerName] = useState<string | null>(null)
  const [referralChecked, setReferralChecked] = useState(false)

  // Confirm the code resolves to a real referrer, so the person signing up gets
  // told *now* rather than silently losing the attribution. Debounced, and a
  // failed lookup never blocks the form — the server ignores bad codes too.
  useEffect(() => {
    const code = normalizeReferralCode(referralCode)
    if (!code || isLogin) {
      setReferrerName(null)
      setReferralChecked(false)
      return
    }
    let cancelled = false
    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/public/referral/${code}`)
        if (cancelled) return
        setReferrerName(data.valid ? data.referrer_name || '' : null)
        setReferralChecked(true)
      } catch {
        if (!cancelled) setReferralChecked(false)
      }
    }, 400)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [referralCode, isLogin])

  // A referral link lands people who mean to sign up — open the form for them.
  useEffect(() => {
    if (referralCode && location.pathname === '/register') setIsLogin(false)
  }, [referralCode, location.pathname])

  // Password recovery dialog ("Forgot password?"). We never reveal whether an
  // email exists — the success screen shows regardless once submitted.
  const [forgotOpen, setForgotOpen] = useState(false)
  const [forgotEmail, setForgotEmail] = useState('')
  const [forgotNote, setForgotNote] = useState('')
  const [forgotLoading, setForgotLoading] = useState(false)
  const [forgotSent, setForgotSent] = useState(false)

  const openForgot = () => {
    setForgotEmail(email)
    setForgotNote('')
    setForgotSent(false)
    setForgotOpen(true)
  }

  const handleForgotSubmit = async (e) => {
    e.preventDefault()
    setForgotLoading(true)
    try {
      await axios.post(`${API}/auth/forgot-password`, {
        email: forgotEmail,
        note: forgotNote || undefined,
      })
      setForgotSent(true)
    } catch (error) {
      // A malformed email (422) is the only real failure; anything else we
      // still treat as "sent" so we never leak whether the account exists.
      if (error.response?.status === 422) {
        toast.error(t.authFailedToast)
      } else {
        setForgotSent(true)
      }
    } finally {
      setForgotLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      if (isLogin) {
        await login(email, password)
        toast.success(t.welcomeBackToast)
      } else {
        await register(email, password, name, referralCode)
        toast.success(t.accountCreatedToast)
      }
      navigate(nextPath || '/dashboard')
    } catch (error) {
      // FastAPI validation failures (422 — e.g. an unroutable email domain)
      // return `detail` as an array of objects. Passing that straight to the
      // toast rendered an object as a React child and took the whole signup
      // page down, so only a plain string is ever shown.
      const detail = error.response?.data?.detail
      toast.error(typeof detail === 'string' ? detail : t.authFailedToast)
    } finally {
      setLoading(false)
    }
  }

  // CTA / nav buttons bring the visitor to the auth card and focus it.
  const goToAuth = (mode: 'login' | 'signup') => {
    setIsLogin(mode === 'login')
    authRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    setTimeout(() => emailRef.current?.focus(), 450)
  }

  const inputClass =
    'h-12 bg-white dark:bg-[#1e1438] border-[#1b1033]/12 dark:border-white/10 rounded-2xl text-[15px] text-[#1b1033] dark:text-[#f4f0ff] placeholder:text-[#1b1033]/35 dark:placeholder:text-gray-500 focus-visible:ring-2 focus-visible:ring-[#ff4d8d]/25 focus-visible:border-[#ff4d8d]'

  const heroFeatures = [
    { Icon: CalendarCheck, label: t.heroFeatureRsvp },
    { Icon: Gift, label: t.heroFeatureGifts },
    { Icon: Languages, label: t.heroFeatureBilingual },
  ]

  const steps = [
    { title: t.step1Title, body: t.step1Body },
    { title: t.step2Title, body: t.step2Body },
    { title: t.step3Title, body: t.step3Body },
  ]

  // Candy accent chips for the hero feature list (pink / violet / sky).
  const heroChipAccents = [
    'bg-[#ff4d8d]/12 text-[#e63a7d]',
    'bg-[#8b5cf6]/14 text-[#6d28d9]',
    'bg-[#38bdf8]/18 text-[#0284c7]',
  ]

  return (
    <div className="bg-story-gradient min-h-[100dvh] text-[#1b1033] dark:text-[#f4f0ff]">
      {/* Sticky nav */}
      <header className="sticky top-0 z-40 border-b border-[#1b1033]/8 bg-[#fff7f2]/85 backdrop-blur-md dark:border-white/10 dark:bg-[#130b26]/85">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3.5">
          <div className="flex items-center gap-7">
            {/* Logo links home; nav mirrors the marketing header so Home/About
                are reachable from the sign-in page too. */}
            <Link
              to="/"
              aria-label="Storyfy — home"
              data-testid="login-brand-logo"
              className="flex items-center gap-2.5 rounded-lg transition-opacity hover:opacity-80"
            >
              <span className="flex h-9 w-9 -rotate-6 items-center justify-center rounded-xl bg-grad-juicy shadow-[0_8px_18px_rgba(255,77,141,0.4)]">
                <Calendar className="h-5 w-5 text-white" />
              </span>
              <span className="font-display text-lg font-semibold tracking-tight">
                Storyfy
              </span>
            </Link>
            <nav className="hidden items-center gap-6 md:flex">
              <Link
                to="/"
                className="text-sm font-medium text-[#4a3d66] transition-colors hover:text-[#ff4d8d] dark:text-[#c3b8dd] dark:hover:text-white"
              >
                {language === 'ka' ? 'მთავარი' : 'Home'}
              </Link>
              <Link
                to="/about"
                className="text-sm font-medium text-[#4a3d66] transition-colors hover:text-[#ff4d8d] dark:text-[#c3b8dd] dark:hover:text-white"
              >
                {language === 'ka' ? 'ჩვენ შესახებ' : 'About'}
              </Link>
            </nav>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <LanguageToggle />
            <ThemeMenu />
            <Button
              onClick={() => goToAuth('login')}
              variant="ghost"
              className="hidden rounded-full px-4 text-[#1b1033] hover:bg-[#1b1033]/[0.05] hover:text-[#ff4d8d] dark:text-[#f4f0ff] dark:hover:bg-white/5 sm:inline-flex"
            >
              {t.signIn}
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        {/* Ambient candy glow */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
          <div
            className="login-aurora absolute -left-[15%] -top-[30%] h-[80%] w-[60%] rounded-full blur-3xl"
            style={{ background: 'radial-gradient(circle at 40% 40%, rgba(255,77,141,0.22), transparent 65%)' }}
          />
          <div
            className="login-aurora absolute right-[-10%] top-[10%] h-[70%] w-[55%] rounded-full blur-3xl"
            style={{ background: 'radial-gradient(circle at 60% 40%, rgba(139,92,246,0.16), transparent 65%)', animationDelay: '-8s' }}
          />
        </div>

        <div className="relative mx-auto grid max-w-6xl items-center gap-12 px-5 py-14 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16 lg:py-24">
          {/* Marketing column */}
          <div className="motion-safe:animate-slide-up">
            <h1 className="font-display font-bold tracking-tight text-balance text-[clamp(2.6rem,6vw,4.5rem)] leading-[1.02]">
              {t.brandHeadlinePrefix}
              <br />
              {t.brandHeadlineMiddle}{' '}
              <span className="text-gradient-brand">{t.brandHeadlineStory}</span>
            </h1>
            <p className="mt-5 max-w-[44ch] text-lg leading-relaxed text-[#4a3d66] dark:text-[#c3b8dd]">
              {t.brandDescription}
            </p>

            <ul className="mt-7 flex flex-wrap gap-x-6 gap-y-3">
              {heroFeatures.map(({ Icon, label }, i) => (
                <li key={label} className="flex items-center gap-2 text-sm font-medium text-[#4a3d66] dark:text-[#c3b8dd]">
                  <span
                    className={`flex h-7 w-7 items-center justify-center rounded-lg ${heroChipAccents[i % heroChipAccents.length]}`}
                  >
                    <Icon className="h-4 w-4" />
                  </span>
                  {label}
                </li>
              ))}
            </ul>

            <div className="mt-9 flex flex-wrap gap-3">
              <Button
                onClick={() => goToAuth('signup')}
                className="group h-12 rounded-full btn-gradient px-6 text-[15px] font-semibold text-white"
                data-testid="hero-get-started-btn"
              >
                {t.heroCtaPrimary}
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Button>
              <Button
                onClick={() => goToAuth('login')}
                variant="outline"
                className="h-12 rounded-full border-transparent bg-white px-6 text-[15px] font-semibold text-[#1b1033] shadow-celebrate-sm hover:bg-white hover:shadow-celebrate-md dark:border-white/15 dark:bg-[#1e1438] dark:text-[#f4f0ff] dark:hover:bg-white/5"
              >
                {t.signIn}
              </Button>
            </div>
          </div>

          {/* Auth card */}
          <div
            ref={authRef}
            className="motion-safe:animate-pop-in scroll-mt-24 rounded-bubble-lg border border-white/60 bg-white p-6 shadow-celebrate-lg dark:border-white/10 dark:bg-[#1e1438] sm:p-8"
          >
            <h2 className="font-display text-2xl font-bold tracking-tight">
              {isLogin ? t.welcomeBack : t.createAccount}
            </h2>
            <p className="mt-1.5 text-sm text-[#4a3d66] dark:text-[#c3b8dd]">
              {isLogin ? t.loginSubtitle : t.registerSubtitle}
            </p>

            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              {!isLogin && (
                <div>
                  <Label
                    htmlFor="name"
                    className="mb-1.5 block text-[13px] font-medium text-[#1b1033]/70 dark:text-[#c3b8dd]"
                  >
                    {t.fullName}
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t.fullNamePlaceholder}
                    className={inputClass}
                    required={!isLogin}
                    data-testid="register-name-input"
                  />
                </div>
              )}

              <div>
                <Label
                  htmlFor="email"
                  className="mb-1.5 block text-[13px] font-medium text-[#1b1033]/70 dark:text-[#c3b8dd]"
                >
                  {t.email}
                </Label>
                <Input
                  ref={emailRef}
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t.emailPlaceholder}
                  className={inputClass}
                  required
                  data-testid="login-email-input"
                />
              </div>

              <div>
                <div className="mb-1.5 flex items-center justify-between gap-2">
                  <Label
                    htmlFor="password"
                    className="block text-[13px] font-medium text-[#1b1033]/70 dark:text-[#c3b8dd]"
                  >
                    {t.password}
                  </Label>
                  {isLogin && (
                    <button
                      type="button"
                      onClick={openForgot}
                      className="text-[13px] font-medium text-[#ff4d8d] underline-offset-4 hover:underline"
                      data-testid="forgot-password-link"
                    >
                      {t.forgotPassword}
                    </button>
                  )}
                </div>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className={inputClass}
                  required
                  data-testid="login-password-input"
                />
              </div>

              {!isLogin && (
                <div>
                  <Label
                    htmlFor="referral_code"
                    className="mb-1.5 block text-[13px] font-medium text-[#1b1033]/70 dark:text-[#c3b8dd]"
                  >
                    {t.referralCodeLabel}
                  </Label>
                  <div className="relative">
                    <Ticket className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#1b1033]/35 dark:text-gray-500" />
                    <Input
                      id="referral_code"
                      type="text"
                      value={referralCode}
                      onChange={(e) =>
                        setReferralCode(normalizeReferralCode(e.target.value))
                      }
                      placeholder={t.referralCodePlaceholder}
                      className={`${inputClass} pl-10 tracking-[0.18em] uppercase`}
                      maxLength={24}
                      autoComplete="off"
                      spellCheck={false}
                      data-testid="register-referral-input"
                    />
                  </div>
                  {referralCode && referralChecked && (
                    <p
                      className={`mt-1.5 flex items-center gap-1.5 text-[13px] ${
                        referrerName === null
                          ? 'text-[#4a3d66] dark:text-[#c3b8dd]'
                          : 'text-emerald-600 dark:text-emerald-400'
                      }`}
                      data-testid="referral-code-status"
                    >
                      {referrerName === null ? (
                        t.referralCodeUnknown
                      ) : (
                        <>
                          <Check className="h-3.5 w-3.5" />
                          {referrerName
                            ? t.referralCodeFrom.replace('{name}', referrerName)
                            : t.referralCodeValid}
                        </>
                      )}
                    </p>
                  )}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="group h-12 w-full rounded-full btn-gradient text-[15px] font-semibold text-white"
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    {isLogin ? t.signIn : t.createAccount}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </span>
                )}
              </Button>
            </form>

            {/* Rules either side of the label rather than a solid chip masking
                a full-width line — the card is translucent over the gradient,
                so an opaque background reads as a stray white pill. */}
            <div className="my-6 flex items-center gap-3 text-[13px] text-[#4a3d66] dark:text-[#c3b8dd]">
              <span className="h-px flex-1 bg-[#1b1033]/10 dark:bg-white/10" />
              {t.orDivider}
              <span className="h-px flex-1 bg-[#1b1033]/10 dark:bg-white/10" />
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={loginWithGoogle}
              className="h-12 w-full rounded-full border-[#1b1033]/12 bg-white font-semibold text-[#1b1033] transition-all hover:bg-[#1b1033]/[0.03] dark:border-white/10 dark:bg-[#1e1438] dark:text-[#f4f0ff] dark:hover:bg-white/5"
              data-testid="google-login-btn"
            >
              <svg className="mr-3 h-5 w-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              {t.continueWithGoogle}
            </Button>

            {/* Consent notice. Sits below both the form and the Google button
                because either one can create an account, and it stays visible
                in sign-in mode too — Google sign-in registers a new user from
                there as well. */}
            <p className="mt-5 text-center text-[13px] leading-relaxed text-[#4a3d66]/85 dark:text-[#c3b8dd]/85">
              {t.authLegalPrefix}{' '}
              <Link
                to="/terms"
                className="font-medium text-[#ff4d8d] underline-offset-4 hover:underline"
                data-testid="auth-terms-link"
              >
                {t.authLegalTerms}
              </Link>{' '}
              {t.authLegalConnector}{' '}
              <Link
                to="/privacy"
                className="font-medium text-[#ff4d8d] underline-offset-4 hover:underline"
                data-testid="auth-privacy-link"
              >
                {t.authLegalPrivacy}
              </Link>
              .
            </p>

            <p className="mt-6 text-center text-[15px] text-[#4a3d66] dark:text-[#c3b8dd]">
              {isLogin ? t.dontHaveAccount : t.alreadyHaveAccount}{' '}
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="font-semibold text-[#ff4d8d] underline-offset-4 hover:underline"
                data-testid="toggle-auth-mode"
              >
                {isLogin ? t.signUp : t.signIn}
              </button>
            </p>
          </div>
        </div>
      </section>

      {/* Product preview — what guests actually see */}
      <section className="border-t border-[#1b1033]/10 dark:border-white/10">
        <div className="mx-auto grid max-w-6xl items-center gap-12 px-5 py-16 lg:grid-cols-2 lg:gap-16 lg:py-24">
          <div>
            <h2 className="font-display text-balance text-[clamp(1.9rem,3.5vw,2.75rem)] font-bold tracking-tight">
              {t.previewTitle}
            </h2>
            <p className="mt-4 max-w-[52ch] text-lg leading-relaxed text-[#4a3d66] dark:text-[#c3b8dd]">
              {t.previewBody}
            </p>
            <ul className="mt-7 space-y-3">
              {[t.previewPoint1, t.previewPoint2, t.previewPoint3].map((point) => (
                <li key={point} className="flex items-center gap-3 text-[15px] text-[#1b1033] dark:text-[#f4f0ff]">
                  <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-grad-hot text-white">
                    <Check className="h-3.5 w-3.5" />
                  </span>
                  {point}
                </li>
              ))}
            </ul>
          </div>

          {/* The example invitations, in the same floating slideshow the
              landing hero plays. */}
          <div className="flex justify-center">
            <div className="w-full max-w-[300px]">
              <Tilt max={8}>
                <ShowcaseSlideshow />
              </Tilt>
            </div>
          </div>
        </div>
      </section>

      {/* How it works — a real 3-step sequence */}
      <section className="border-t border-[#1b1033]/10 dark:border-white/10">
        <div className="mx-auto max-w-6xl px-5 py-16 lg:py-24">
          <div className="max-w-[44ch]">
            <h2 className="font-display text-[clamp(1.9rem,3.5vw,2.75rem)] font-bold tracking-tight">
              {t.stepsTitle}
            </h2>
            <p className="mt-3 text-lg text-[#4a3d66] dark:text-[#c3b8dd]">{t.stepsSubtitle}</p>
          </div>
          <ol className="mt-12 grid gap-6 sm:grid-cols-3 sm:gap-7">
            {steps.map((step, i) => (
              <li
                key={step.title}
                className="group tile relative overflow-hidden rounded-bubble p-8"
              >
                <span
                  className={`relative flex h-14 w-14 items-center justify-center rounded-[18px] font-display text-2xl font-bold tabular-nums transition-transform duration-300 ease-spring group-hover:-rotate-6 group-hover:scale-110 motion-reduce:transition-none ${
                    ['text-[#e63a7d]', 'text-[#6d28d9]', 'text-[#b45309]'][i]
                  }`}
                  style={{
                    background: [
                      'linear-gradient(135deg, #ffe0ec, #ffd0c2)',
                      'linear-gradient(135deg, #e9defb, #d7ecff)',
                      'linear-gradient(135deg, #fdf0c8, #d6f7e8)',
                    ][i],
                  }}
                >
                  {i + 1}
                </span>
                <h3 className="relative mt-5 font-display text-xl font-bold tracking-tight">
                  {step.title}
                </h3>
                <p className="relative mt-2 max-w-[34ch] text-[15px] leading-relaxed text-[#4a3d66] dark:text-[#c3b8dd]">
                  {step.body}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Closing CTA — juicy gradient drench */}
      <section className="relative overflow-hidden bg-grad-juicy">
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          <div
            className="blob"
            style={{ width: 380, height: 380, background: '#fff', opacity: 0.22, top: -160, left: '8%' }}
          />
          <div
            className="blob"
            style={{ width: 320, height: 320, background: '#fff', opacity: 0.18, bottom: -150, right: '6%' }}
          />
        </div>
        <div className="relative mx-auto max-w-6xl px-5 py-16 text-center lg:py-20">
          <h2 className="mx-auto max-w-[20ch] font-display text-balance text-[clamp(2rem,4vw,3rem)] font-bold leading-[1.05] tracking-tight text-white">
            {t.ctaTitle}
          </h2>
          <p className="mx-auto mt-4 max-w-[48ch] text-lg text-white/85">{t.ctaBody}</p>
          <Magnetic className="mt-8 inline-block">
            <Button
              onClick={() => goToAuth('signup')}
              className="group h-12 rounded-full bg-white px-7 text-[15px] font-semibold text-[#1b1033] shadow-celebrate-sm transition-shadow hover:bg-white/90 hover:shadow-celebrate-md"
              data-testid="cta-get-started-btn"
            >
              {t.heroCtaPrimary}
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Button>
          </Magnetic>
        </div>
      </section>

      <AppFooter maxWidth="max-w-6xl" />

      {/* Forgot-password recovery */}
      <Dialog
        open={forgotOpen}
        onOpenChange={(o) => {
          if (!o && !forgotLoading) setForgotOpen(false)
        }}
      >
        <DialogContent className="rounded-bubble border-[#1b1033]/10 bg-white dark:border-white/10 dark:bg-[#1e1438]">
          {forgotSent ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-[#1b1033] dark:text-[#f4f0ff]">
                  <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
                  {t.resetRequestSentTitle}
                </DialogTitle>
                <DialogDescription className="text-[#4a3d66] dark:text-[#c3b8dd]">
                  {t.resetRequestSentBody}
                </DialogDescription>
              </DialogHeader>
              <Button
                type="button"
                onClick={() => setForgotOpen(false)}
                className="h-11 w-full rounded-full btn-gradient font-semibold text-white"
                data-testid="forgot-done-btn"
              >
                {t.close}
              </Button>
            </>
          ) : (
            <form onSubmit={handleForgotSubmit}>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-[#1b1033] dark:text-[#f4f0ff]">
                  <KeyRound className="h-5 w-5 text-[#ff4d8d]" />
                  {t.resetPasswordTitle}
                </DialogTitle>
                <DialogDescription className="text-[#4a3d66] dark:text-[#c3b8dd]">
                  {t.resetPasswordSubtitle}
                </DialogDescription>
              </DialogHeader>
              <div className="mt-4 space-y-4">
                <div>
                  <Label
                    htmlFor="forgot-email"
                    className="mb-1.5 block text-[13px] font-medium text-[#1b1033]/70 dark:text-[#c3b8dd]"
                  >
                    {t.resetPasswordEmailLabel}
                  </Label>
                  <Input
                    id="forgot-email"
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder={t.emailPlaceholder}
                    className={inputClass}
                    required
                    autoFocus
                    data-testid="forgot-email-input"
                  />
                </div>
                <div>
                  <Label
                    htmlFor="forgot-note"
                    className="mb-1.5 block text-[13px] font-medium text-[#1b1033]/70 dark:text-[#c3b8dd]"
                  >
                    {t.resetPasswordNoteLabel}
                  </Label>
                  <Textarea
                    id="forgot-note"
                    value={forgotNote}
                    onChange={(e) => setForgotNote(e.target.value)}
                    placeholder={t.resetPasswordNotePlaceholder}
                    rows={3}
                    maxLength={500}
                    className="rounded-2xl border-[#1b1033]/12 bg-white text-[15px] text-[#1b1033] dark:border-white/10 dark:bg-[#1e1438] dark:text-[#f4f0ff]"
                    data-testid="forgot-note-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={forgotLoading}
                  className="h-12 w-full rounded-full btn-gradient text-[15px] font-semibold text-white"
                  data-testid="forgot-submit-btn"
                >
                  {forgotLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    t.sendResetRequest
                  )}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default Login
